import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import newimage from "../assets/newimage.png";
import Headingcomp from '../components/Headingcomp';
import { getProducts } from '../api/products';
import AnimateInView from '../components/AnimateInView';
import "../components/css/Gallery.css"
import Talk from '../components/Talk';
import { demoProducts } from '../data/demoProducts';

const Gallery = () => {
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const response = await getProducts();
                
                if (!response || !response.data) {
                    throw new Error('No data received from server');
                }

                const fetchedProducts = response.data.products || response.data;
                
                // Validate product data
                const validProducts = fetchedProducts.filter(product => 
                    product && 
                    product.image && 
                    product.image.url && 
                    product.title && 
                    product._id
                );

                // Combine with demo products if needed
                const finalProducts = validProducts.length < 4 
                    ? [...validProducts, ...demoProducts.slice(0, 10 - validProducts.length)]
                    : validProducts;

                setProducts(finalProducts);
                setError(null);
            } catch (error) {
                console.error('Error fetching products:', error);
                setError('');
                setProducts(demoProducts);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    const openFullscreen = (product) => {
        if (!product) return;
        setSelectedProduct(product);
        setIsFullscreen(true);
        document.body.style.overflow = 'hidden';
    };

    const closeFullscreen = () => {
        setSelectedProduct(null);
        setIsFullscreen(false);
        document.body.style.overflow = 'auto';
    };

    const renderGalleryItem = (product) => {
        if (!product?.image?.url || !product.title) return null;

        return (
            <div
                key={product._id}
                className="gallery-item"
                onClick={() => openFullscreen(product)}
            >
                <img 
                    src={product.image.url} 
                    alt={product.title}
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = newimage; // Fallback image
                    }}
                />
                <div className="gallery-overlay">
                    <h3 className="heading-small">{product.title}</h3>
                    <p className="paragraph">{product.description}</p>
                </div>
            </div>
        );
    };

    return (
        <main>
            <Helmet>
                <title>Gallery | Dhiman Wood Works Craftsmanship</title>
                <meta 
                    name="description" 
                    content="Explore our collection of handcrafted wooden furniture and custom woodwork. View our latest designs and craftsmanship." 
                />
            </Helmet>

            <Headingcomp
                heading="CRAFTSMANSHIP GALLERY"
                subheading="MASTERPIECES IN WOOD"
                image={newimage}
            />

            {loading && <div className="loading">Loading gallery...</div>}
            
            {error && <div className="error-message">{error}</div>}

            <div className="gallery-container">
                <div className="gallery-grid">
                    {products.map(product => renderGalleryItem(product))}
                </div>
            </div>

            {isFullscreen && selectedProduct && (
                <div className="fullscreen-modal" onClick={closeFullscreen}>
                    <div className="fullscreen-content" onClick={e => e.stopPropagation()}>
                        <button className="close-button" onClick={closeFullscreen}>×</button>
                        <div className="fullscreen-image-container">
                            <img
                                src={selectedProduct.image.url}
                                alt={selectedProduct.title}
                                className="fullscreen-image"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = newimage;
                                }}
                            />
                        </div>
                        <div className="fullscreen-details">
                            <h2 className="heading-small">{selectedProduct.title}</h2>
                            <p className="paragraph">{selectedProduct.description}</p>
                            {selectedProduct.features && (
                                <div className="features-list">
                                    {selectedProduct.features.map((feature, index) => (
                                        <span key={index} className="feature-tag">{feature}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <AnimateInView delay={0.2}>
                <Talk />
            </AnimateInView>
        </main>
    );
};

export default Gallery;
